import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    applicationBaseName,
    buildApplicationPdfBlob,
    buildApplicationZipBlob,
    saveBlob,
} from '../helpers/applicationExport';
import type { ApplicationConfigType, ApplicationItemType, FormDataType } from '../helpers/types';

type Props = {
    application: ApplicationItemType;
    applicationConfig: ApplicationConfigType | null;
    formData: FormDataType;
    statusLabel: string;
};

type Busy = null | 'pdf' | 'zip';

/**
 * «Skriv ut»-menyen på Søknadsinfo.
 *
 * Tre utganger på samme datagrunnlag:
 *   1. Utskrift  — `window.print()` mot print-CSS-en i App.css
 *   2. PDF       — @react-pdf/renderer, lastet dynamisk først når den trengs
 *   3. ZIP       — PDF + vedleggene søker har tilgang til
 */
const ApplicationPrintMenu = ({ application, applicationConfig, formData, statusLabel }: Props) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState<Busy>(null);
    const [progress, setProgress] = useState<string | null>(null);
    const [message, setMessage] = useState<{ tone: 'warning' | 'danger'; text: string } | null>(null);

    const pdfInput = { application, applicationConfig, formData, statusLabel, t };

    const handlePrint = () => {
        setMessage(null);
        window.print();
    };

    const handlePdf = async () => {
        setMessage(null);
        setBusy('pdf');
        try {
            const blob = await buildApplicationPdfBlob(pdfInput);
            saveBlob(blob, `${applicationBaseName(application)}.pdf`);
        } catch (error) {
            console.error('Kunne ikke lage PDF:', error);
            setMessage({ tone: 'danger', text: t('print_pdf_failed') });
        } finally {
            setBusy(null);
        }
    };

    const handleZip = async () => {
        setMessage(null);
        setBusy('zip');
        try {
            const { blob, result } = await buildApplicationZipBlob(pdfInput, (done, total) =>
                setProgress(total > 0 ? `${done}/${total}` : null),
            );
            saveBlob(blob, `${applicationBaseName(application)}.zip`);

            if (result.failed.length > 0) {
                // Manuell interpolasjon med vilje: `count` er et reservert
                // i18next-navn som slår om til flertallsnøkler
                // (`..._one`/`..._other`), og de finnes ikke her.
                setMessage({
                    tone: 'warning',
                    text: t('print_zip_partial').replace('{{count}}', String(result.failed.length)),
                });
            }
        } catch (error) {
            console.error('Kunne ikke pakke søknaden:', error);
            setMessage({ tone: 'danger', text: t('print_zip_failed') });
        } finally {
            setBusy(null);
            setProgress(null);
        }
    };

    return (
        <div className="no-print">
            <Dropdown align="end">
                <Dropdown.Toggle
                    variant="outline-primary"
                    size="sm"
                    id="application-print-menu"
                    disabled={busy !== null}
                    className="w-100"
                >
                    {busy ? (
                        <>
                            <FontAwesomeIcon icon={['fad', 'spinner-third']} spin className="me-2" />
                            {busy === 'zip' && progress ? `${t('print_working')} ${progress}` : t('print_working')}
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={['far', 'print']} className="me-2" />
                            {t('print_menu')}
                        </>
                    )}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                    <Dropdown.Item onClick={handlePrint}>
                        <FontAwesomeIcon icon={['far', 'print']} className="me-2 text-muted" fixedWidth />
                        {t('print_application')}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handlePdf}>
                        <FontAwesomeIcon icon={['far', 'file-pdf']} className="me-2 text-muted" fixedWidth />
                        {t('print_download_pdf')}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleZip}>
                        <FontAwesomeIcon icon={['far', 'file-zipper']} className="me-2 text-muted" fixedWidth />
                        {t('print_download_zip')}
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>

            {message ? (
                <div className={`small mt-2 text-${message.tone === 'danger' ? 'danger' : 'warning-emphasis'}`}>
                    <FontAwesomeIcon icon={['far', 'circle-exclamation']} className="me-1" />
                    {message.text}
                </div>
            ) : null}
        </div>
    );
};

export default ApplicationPrintMenu;
